/**
 * Ask the browser to keep our origin's storage (IndexedDB, Cache
 * Storage, localStorage) OUT of the automatic-eviction pool. Without
 * this the browser is free to evict us under quota pressure — a
 * critical failure for offline sync (queued POS commits get purged
 * before the tenant reconnects). With it, the origin can only be
 * cleared by explicit user action from browser settings.
 *
 * Chrome auto-grants when engagement heuristics pass (visited
 * frequently, PWA installed, notifications granted). Safari + Firefox
 * prompt the user the first time we call. Never throws — the caller
 * has no recovery if the grant is refused, only "your offline data
 * may be evicted under storage pressure" as a user-visible warning
 * we can surface elsewhere.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (typeof navigator === 'undefined') return false;
  if (!navigator.storage?.persist) return false;
  try {
    // Skip the request when we already have the grant — avoids a
    // second user prompt on browsers that surface one.
    const already = await navigator.storage.persisted?.();
    if (already) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
